import { useCallback, useEffect, useRef } from 'react';

export type SaleInvalidationOptions = {
  variantId?: string;
  pins?: boolean;
  products?: boolean;
  orderToday?: boolean;
};

export type SaleInvalidationActions = {
  refreshPins?: () => void | Promise<void>;
  refreshSaleProduct?: () => void | Promise<void>;
  refreshOrderToday?: () => void | Promise<void>;
  loadVariantStock?: (variantId: string) => void | Promise<void>;
  fetchLiveVideos?: () => void | Promise<void>;
};

const INVALIDATION_DELAY_MS = 400;

export function useSaleInvalidation() {
  const actionsRef = useRef<SaleInvalidationActions>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef({
    variantIds: new Set<string>(),
    pins: false,
    products: false,
    orderToday: false,
  });

  const registerActions = useCallback((actions: SaleInvalidationActions) => {
    actionsRef.current = { ...actionsRef.current, ...actions };
  }, []);

  const flushInvalidation = useCallback(async () => {
    const pending = pendingRef.current;
    pendingRef.current = {
      variantIds: new Set(),
      pins: false,
      products: false,
      orderToday: false,
    };

    const actions = actionsRef.current;
    const variantIds = [...pending.variantIds];

    for (const variantId of variantIds) {
      await actions.loadVariantStock?.(variantId);
    }
    if (pending.pins) {
      await actions.refreshPins?.();
    }
    if (pending.products) {
      await actions.refreshSaleProduct?.();
    }
    if (pending.orderToday) {
      await actions.refreshOrderToday?.();
    }
  }, []);

  const scheduleInvalidation = useCallback(
    (options: SaleInvalidationOptions = {}) => {
      const pending = pendingRef.current;
      if (options.variantId) {
        pending.variantIds.add(options.variantId);
      }
      if (options.pins) pending.pins = true;
      if (options.products) pending.products = true;
      if (options.orderToday) pending.orderToday = true;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void flushInvalidation();
      }, INVALIDATION_DELAY_MS);
    },
    [flushInvalidation],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return {
    registerActions,
    scheduleInvalidation,
    flushInvalidation,
  };
}
